import { useState, useCallback, useEffect } from 'react';
import type { AnsweredBy } from '../../../utils/badgeStyles';
import { useQuery as useApolloQuery } from '@apollo/client/react';
import { GET_FLOW_CALLS } from '../../../lib/graphql/queries/organizations';

const PAGE_SIZE = 25;

interface GQLCallSummary {
  callId: string;
  contactId: string | null;
  contactName: string | null;
  conversationId: string | null;
  direction: string | null;
  inbound: boolean | null;
  fromNumber: string | null;
  toNumber: string | null;
  status: string | null;
  outcome: string | null;
  answeredBy: string | null;
  durationSeconds: number | null;
  initiatedAt: string | null;
  endedAt: string | null;
  summary: string | null;
  transcript: string | null;
  sentiment: string | null;
  recordingUrl: string | null;
  agentId: string | null;
  flowName: string | null;
  flowType: string | null;
  callAnalysisTimeline: string | null;
  callAnalysisAnswers: string | null;
  isTest: boolean | null;
  createdAt: string | null;
}

// Call shape expected by FlowCallRecordsModal and CallDetailsPanel
export interface GQLCall extends Record<string, unknown> {
  id: string;
  call_id: string;
  inbound?: boolean;
  answered_by?: AnsweredBy;
  callTimestamp?: string;
  to_number?: string;
  from_number?: string;
  recordingUrl?: string;
  status?: string;
  duration?: number;
  corrected_duration?: number;
  flowName?: string;
  flowType?: string;
  contactId?: string;
  contact?: undefined;
  fullName?: string;
  call_analysis?: {
    outcome: string;
    notes?: string;
    timeline?: Array<{ event: string; timestamp: string }>;
    answers?: Record<string, string | undefined>;
    summary?: string;
  };
  concatenated_transcript?: string;
  isTest?: boolean;
}

interface GetFlowCallsResult {
  flowCalls: {
    items: GQLCallSummary[];
    total: number;
    hasMore: boolean;
  };
}

function tryParseJSON<T>(value: string | null | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function mapGQLCall(c: GQLCallSummary): GQLCall {
  const timeline = tryParseJSON<Array<{ event: string; timestamp: string }>>(
    c.callAnalysisTimeline,
    []
  );
  const answers = tryParseJSON<Record<string, string | undefined>>(c.callAnalysisAnswers, {});

  return {
    id: c.callId,
    call_id: c.callId,
    inbound: c.inbound ?? false,
    answered_by: (c.answeredBy ?? undefined) as AnsweredBy,
    callTimestamp: c.initiatedAt ?? undefined,
    to_number: c.toNumber ?? undefined,
    from_number: c.fromNumber ?? undefined,
    recordingUrl: c.recordingUrl ?? undefined,
    status: c.status ?? undefined,
    duration: c.durationSeconds ?? undefined,
    corrected_duration: c.durationSeconds ?? undefined,
    flowName: c.flowName ?? undefined,
    flowType: c.flowType ?? undefined,
    contactId: c.contactId ?? undefined,
    contact: undefined,
    fullName: c.contactName || c.toNumber || '-',
    concatenated_transcript: c.transcript ?? undefined,
    call_analysis: {
      outcome: c.outcome ?? '',
      timeline,
      answers,
      summary: c.summary ?? undefined,
    },
    isTest: c.isTest ?? false,
  };
}

interface UseFlowCallsGQLParams {
  isOpen: boolean;
  flowId: string;
  organizationId: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  search?: string;
}

export const useFlowCallsGQL = ({
  isOpen,
  flowId,
  organizationId,
  sortField,
  sortDirection = 'desc',
  search,
}: UseFlowCallsGQLParams) => {
  const [currentPage, setCurrentPage] = useState(0);

  const apiSortBy = sortField === 'corrected_duration' ? 'durationSeconds'
    : sortField === 'answered_by' ? 'answeredBy'
    : sortField === 'callTimestamp' ? 'initiatedAt'
    : 'initiatedAt';

  const { data, loading, error } = useApolloQuery<GetFlowCallsResult>(
    GET_FLOW_CALLS,
    {
      variables: {
        organizationId,
        flowId,
        limit: PAGE_SIZE,
        offset: currentPage * PAGE_SIZE,
        sortBy: apiSortBy,
        sortOrder: sortDirection.toUpperCase(),
        search: search || undefined,
      },
      skip: !isOpen || !organizationId || !flowId,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  // Reset to page 0 when flow, sort, or search changes
  useEffect(() => {
    setCurrentPage(0);
  }, [flowId, sortField, sortDirection, search]);

  const calls = (data?.flowCalls?.items ?? []).map(mapGQLCall);
  const total = data?.flowCalls?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const nextPage = useCallback(() => setCurrentPage((p) => Math.min(p + 1, totalPages - 1)), [totalPages]);
  const prevPage = useCallback(() => setCurrentPage((p) => Math.max(0, p - 1)), []);
  const goToPage = useCallback((page: number) => setCurrentPage(page), []);

  const setCalls = useCallback((_updater: GQLCall[] | ((prev: GQLCall[]) => GQLCall[])) => {
    // no-op: calls are derived from GQL data
  }, []);

  return {
    calls,
    setCalls,
    isLoading: loading,
    error: error ?? null,
    total,
    totalPages,
    currentPage,
    pageSize: PAGE_SIZE,
    nextPage,
    prevPage,
    goToPage,
    // legacy compat
    hasMore: data?.flowCalls?.hasMore ?? false,
    loadMore: nextPage,
    totalCalls: total,
  };
};
